// Locally-saved stories (this browser only, no account). Keyed by the primary article id so the Arabic and
// English pages of one story never create two entries. Persisted in localStorage; changes broadcast via
// the `saved:change` window event so the nav counter and the Saved page stay in sync.
import { useEffect, useState } from "react";

const KEY = "saved_articles_v1";

export function listSaved() {
    try {
        const raw = localStorage.getItem(KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr.filter((x) => x && x.id) : [];
    } catch { return []; }
}

export const isSaved = (id) => listSaved().some((x) => x.id === id);

function persist(arr) {
    // Throws when storage is unavailable/full → caller shows an error, never a fake success.
    localStorage.setItem(KEY, JSON.stringify(arr));
    const back = localStorage.getItem(KEY);
    if (back !== JSON.stringify(arr)) throw new Error("storage_verify_failed");
    try { window.dispatchEvent(new CustomEvent("saved:change", { detail: { count: arr.length } })); } catch { /* noop */ }
}

/** Save a story. Returns {ok:true} after the write is VERIFIED, or {ok:false, error}. */
export function saveArticle({ id, slug, title }) {
    try {
        const arr = listSaved().filter((x) => x.id !== id);
        arr.unshift({ id, slug, title: (title || "").slice(0, 200), saved_at: new Date().toISOString() });
        persist(arr);
        return { ok: true };
    } catch (e) { return { ok: false, error: e?.name || "storage_error" }; }
}

export function removeSaved(id) {
    try { persist(listSaved().filter((x) => x.id !== id)); return { ok: true }; }
    catch (e) { return { ok: false, error: e?.name || "storage_error" }; }
}

export function useSavedList() {
    const [items, setItems] = useState(listSaved);
    useEffect(() => {
        const refresh = () => setItems(listSaved());
        window.addEventListener("saved:change", refresh);
        window.addEventListener("storage", refresh);
        return () => { window.removeEventListener("saved:change", refresh); window.removeEventListener("storage", refresh); };
    }, []);
    return items;
}
